import React, { useEffect, useMemo, useRef, useState } from "react";

const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeOption = (option) => {
  if (option && typeof option === "object") {
    return {
      value: option.value,
      label: String(option.label ?? option.value ?? "").trim(),
    };
  }

  return {
    value: option,
    label: String(option || "").trim(),
  };
};

const SearchableAddDropdown = ({
  value,
  options,
  onChange,
  onAddOption,
  placeholder = "Select...",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const normalizedOptions = useMemo(
    () =>
      Array.isArray(options)
        ? options.map(normalizeOption).filter((option) => option.label)
        : [],
    [options],
  );

  const filteredOptions = useMemo(() => {
    const q = normalize(search);
    if (!q) return normalizedOptions;
    return normalizedOptions.filter((option) =>
      normalize(option.label).includes(q),
    );
  }, [normalizedOptions, search]);

  const selectedLabel = useMemo(() => {
    const selected = normalizedOptions.find(
      (option) => String(option.value) === String(value),
    );

    if (selected) return selected.label;

    if (value === null || value === undefined || value === "") return "";
    return String(value);
  }, [normalizedOptions, value]);

  const canAdd = useMemo(() => {
    if (!onAddOption) return false;
    const candidate = String(search || "").trim();
    if (!candidate) return false;
    return !normalizedOptions.some(
      (option) => normalize(option.label) === normalize(candidate),
    );
  }, [onAddOption, search, normalizedOptions]);

  useEffect(() => {
    const onDocumentClick = (event) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleAdd = async () => {
    const candidate = String(search || "").trim();
    if (!candidate || !onAddOption) return;
    const created = await onAddOption(candidate);
    if (created) {
      onChange(candidate);
      setSearch("");
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="w-full px-3 py-2 border border-gray-300 rounded text-left bg-white disabled:bg-gray-100 disabled:cursor-not-allowed flex items-center justify-between"
      >
        <span className={selectedLabel ? "text-gray-900" : "text-gray-400"}>
          {selectedLabel || placeholder}
        </span>
        <span className="text-gray-500">▾</span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded shadow-lg">
          <div className="p-2 border-b border-gray-200">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search or type to add..."
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
            />
          </div>

          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.map((option) => (
              <button
                key={String(option.value)}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                  setSearch("");
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${
                  String(option.value) === String(value)
                    ? "bg-blue-100 text-blue-800"
                    : "text-gray-900"
                }`}
              >
                {option.label}
              </button>
            ))}

            {filteredOptions.length === 0 && !canAdd && (
              <div className="px-3 py-2 text-sm text-gray-500">
                No options found
              </div>
            )}

            {canAdd && (
              <button
                type="button"
                onClick={handleAdd}
                className="w-full text-left px-3 py-2 text-sm text-green-700 hover:bg-green-50 border-t border-gray-200"
              >
                + Add "{String(search || "").trim()}"
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableAddDropdown;
